"use client";
import dynamic from 'next/dynamic';
import { useState } from "react";
import Link from "next/link";
import { MapPin, Mail } from 'lucide-react';
import Image from 'next/image';
import { business, companyLinks, footerCompanyLinks, social, clientSegmentsLinks, capabilitiesLinks } from "@/lib/business";
import CalendlyLink from "./CalendlyLink";
// Performance: Dynamically load the calendar to keep initial JS bundle small
const Calendar = dynamic(() => import("../ui/calendar").then((mod) => mod.Calendar), {
  ssr: false,
  loading: () => <div className="h-[280px] w-[250px] bg-gray-900 animate-pulse rounded-xl" />
});


// Dynamically load Calendly widget to avoid hydration issues
const CalendlyBadge = dynamic(() => import("./CalendlyBadge"), {
  ssr: false,
  loading: () => <div className="w-full h-[700px] bg-gray-900 animate-pulse rounded-xl" />
});

const Footer = () => {
    const [date, setDate] = useState(new Date());
    const currentYear = new Date().getFullYear();

    // SEO: Organization Schema
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Seternity Solutions",
      "url": "https://www.seternitysolutions.com",
      "logo": "https://www.seternitysolutions.com/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+919680585289",
        "contactType": "customer service",
        "email": "support@seternitysolutions.com"
      },
      "sameAs": [
        "https://www.linkedin.com/company/seternity-solutions",
        "https://www.facebook.com/seternitysolutions/"
      ]
    };

    const services = [
        { label: "Accounting & Bookkeeping", href: "/services/accounting-and-bookkeeping" },
        { label: "Market Research", href: "/services/market-research" },
        { label: "Capital Market Research", href: "/services/capital-market-research" },
        { label: "Investment & Transaction Advisory", href: "/services/investment-transaction" },
        { label: "Data Analytics", href: "/services/data-analytics" },
        { label: "Business Development", href: "/services/business-developement" },
        { label: "Email Finder", href: "/email-finder" }
    ];

    return (
        <footer className='font-secondary min-h-fit bg-black border-t border-gray-800 py-16 px-4 sm:px-6 lg:px-8'>
            {/* Invisible SEO Metadata */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />

            <div className='max-w-7xl mx-auto'>
                <div className='grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16'>

                    {/* Left Column: Brand & Calendar */}
                    <div className='lg:col-span-5 flex flex-col space-y-8'>
                        <div className="space-y-4">
                            <h2 className="text-3xl font-bold text-white tracking-tight">
                                Seternity Solutions
                            </h2>
                            <p className='text-white leading-relaxed max-w-md text-sm sm:text-base'>
                                Empowering institutional investors and businesses with data-driven financial insights and strategic transaction support.
                            </p>
                        </div>

                        {/* Calendar - Accessible & Optimized */}
                        {/* <div className="flex justify-start">
                            <div className="inline-block p-1 bg-white rounded-xl shadow-[0_0_20px_rgba(255,255,255,0.1)] scale-90 origin-left sm:scale-100">
                                <Calendar
                                    mode="single"
                                    selected={date}
                                    onSelect={setDate}
                                    className="rounded-lg text-gray-900"
                                />
                            </div>
                        </div>

                        <Link href="/contact" className="w-full sm:w-fit bg-brand hover:bg-brand/80 text-white px-10 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg active:scale-95 text-center">
                            Book a Strategy Call
                        </Link> */}

                        {/* Calendly Badge - Booking Section */}
                        <div className="relative space-y-4">
                            <h3 className="text-lg font-semibold text-white">Speak With our Team</h3>
                            <div className="top-0">
                                {/* <CalendlyBadge /> */}
                                 <CalendlyLink />
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Links */}
                    <div className='lg:col-span-7 flex flex-col space-y-12'>
                        <nav className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8' aria-label="Footer Navigation">

                            {/* Company Section */}
                            <div className='space-y-4'>
                                <h3 className="text-xs font-bold text-white uppercase tracking-[0.2em] pb-2 border-b border-gray-800">Company</h3>
                                <ul className='space-y-3'>
                                    {footerCompanyLinks.map((item) => (
                                        <li key={item.label}>
                                            <Link href={item.href} className="text-white hover:text-white transition-colors duration-200 text-sm inline-block">
                                                {item.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Services Section */}
                            <div className='space-y-4'>
                                <h3 className="text-xs font-bold text-white uppercase tracking-[0.2em] pb-2 border-b border-gray-800">Services</h3>
                                <ul className='space-y-3'>
                                    {capabilitiesLinks.map((item) => (
                                        <li key={item.label}>
                                            <Link href={item.href} className="text-white hover:text-white transition-colors duration-200 text-sm inline-block">
                                                {item.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Client Segments Section */}
                            <div className='space-y-4'>
                                <h3 className="text-xs font-bold text-white uppercase tracking-[0.2em] pb-2 border-b border-gray-800">Client Segments</h3>
                                <ul className='space-y-3'>
                                    {clientSegmentsLinks.map((item) => (
                                        <li key={item.label}>
                                            <Link href={item.href} className="text-white hover:text-white transition-colors duration-200 text-sm inline-block">
                                                {item.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </nav>

                        {/* Contact & Social Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-gray-800">
                            <div className='space-y-4'>
                                <h3 className="text-xs font-bold text-white uppercase tracking-[0.2em]">Contact Us</h3>
                                <div className="space-y-3">
                                    <address className="flex items-start space-x-3 group not-italic">
                                        <MapPin className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                                        <span className="text-white text-sm leading-relaxed transition-colors">
                                            {business.address}
                                        </span>
                                    </address>
                                    <div className="flex items-center space-x-3 group">
                                        <Mail className="h-5 w-5 text-blue-500 flex-shrink-0" aria-hidden="true" />
                                        <a href={`mailto:${business.supportEmail}`} className="text-white hover:text-white text-sm transition-colors truncate">
                                            {business.supportEmail}
                                        </a>
                                    </div>
                                    <div className="flex items-center space-x-3 group">
                                        <Mail className="h-5 w-5 text-blue-500 flex-shrink-0" aria-hidden="true" />
                                        <a href={`mailto:${business.salesEmail}`} className="text-white hover:text-white text-sm transition-colors truncate">
                                            {business.salesEmail}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className='space-y-4'>
                                <h3 className="text-xs font-bold text-white uppercase tracking-[0.2em]">Follow Us</h3>
                                <div className="flex flex-wrap gap-3">
                                    {social.map((item) => (
                                        <a
                                            key={item.title}
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Visit our ${item.title} profile`}
                                            className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-white transition-all transform hover:-translate-y-1"
                                        >
                                            <Image
                                                src={item.image}
                                                alt="" // Alt left empty because aria-label on <a> is better for icons
                                                width={20}
                                                height={20}
                                                className="object-contain"
                                                unoptimized
                                            />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col-reverse md:flex-row items-center justify-between gap-6">
                    <p className="text-xs text-gray-100 text-center md:text-left">
                        &copy; {currentYear} Seternity Solutions. All rights reserved.
                    </p>
                    <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-gray-100" aria-label="Legal">
                        <Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                        <Link href="/terms-and-conditions" className="hover:text-white transition-colors">Terms and Condition</Link>
                        {/* <Link href="/terms-and-conditions" className="hover:text-white transition-colors">Terms and Conditions</Link> */}
                    </nav>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
